
import React, { useCallback, useEffect, useState } from "react";
import Header from "@commons/header";
import Table from "@components/table";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";
import { Audit } from "@apis/index";
import { User, Database, Filter, X, RotateCcw, ArrowRight, Search } from "lucide-react";

// Known logical modules (mirrors the backend audit registry).
const MODULES = [
  "Admins",
  "Users",
  "Catalog",
  "Marketing",
  "Orders",
  "Finance",
  "Settings",
  "Other",
];

// Render any value compactly for the diff cells.
const formatValue = (val: any): string => {
  if (val === null || val === undefined || val === "") return "—";
  if (typeof val === "boolean") return val ? "true" : "false";
  if (typeof val === "object") {
    try {
      const s = JSON.stringify(val);
      return s.length > 40 ? `${s.slice(0, 40)}…` : s;
    } catch {
      return "[object]";
    }
  }
  const s = String(val);
  return s.length > 40 ? `${s.slice(0, 40)}…` : s;
};

const AuditLogTable = () => {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState({
    action: "",
    entity: "",
    module: "",
    search: "",
    startDate: "",
    endDate: "",
  });

  const {
    items: logs = [],
    totalItems,
    totalPages,
    loading,
  } = useSelector((state: RootState) => (state.auditLogs || {}) as any);

  const fetchLogs = useCallback(async () => {
    // Only send non-empty params so we don't over-constrain the query.
    const params: any = { page, limit: 15 };
    Object.entries(filters).forEach(([k, v]) => {
      if (v) params[k] = v;
    });
    await Audit.list({ state: "auditLogs", params });
  }, [page, filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Debounce the free-text search so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchInput.trim() }));
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleFilterChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>,
  ) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      action: "",
      entity: "",
      module: "",
      search: "",
      startDate: "",
      endDate: "",
    });
    setSearchInput("");
    setPage(1);
  };

  const hasActiveFilters =
    filters.action ||
    filters.entity ||
    filters.module ||
    filters.search ||
    filters.startDate ||
    filters.endDate;

  const handleRestore = async (id: number) => {
    if (window.confirm("Are you sure you want to restore this item?")) {
      try {
        await Audit.restore({ id });
        fetchLogs();
      } catch (err) {
        console.error(err);
      }
    }
  };

  const columns = [
    {
      title: "Admin",
      key: "admin",
      width: "15%",
      render: (item: any) => (
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div
            style={{
              background: "#eff6ff",
              padding: "8px",
              borderRadius: "8px",
              color: "#3b82f6",
            }}
          >
            <User size={16} />
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontWeight: 600, fontSize: "14px" }}>
              {item.adminName}
            </span>
            <span style={{ fontSize: "12px", color: "#64748b" }}>
              {item.actorRole ? `${item.actorRole} · ` : ""}ID: {item.adminId}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: "11%",
      render: (item: any) => {
        const colors: any = {
          CREATE: { bg: "#dcfce7", text: "#166534" },
          UPDATE: { bg: "#fef9c3", text: "#854d0e" },
          DELETE: { bg: "#fee2e2", text: "#991b1b" },
          APPROVE: { bg: "#dcfce7", text: "#166534" },
          REJECT: { bg: "#fee2e2", text: "#991b1b" },
          VERIFY: { bg: "#dbeafe", text: "#1e40af" },
          STATUS_UPDATE: { bg: "#ede9fe", text: "#5b21b6" },
        };
        const style = colors[item.action] || { bg: "#f1f5f9", text: "#475569" };
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span
              style={{
                backgroundColor: style.bg,
                color: style.text,
                padding: "4px 10px",
                borderRadius: "6px",
                fontSize: "11px",
                fontWeight: 600,
                textTransform: "uppercase",
                width: "fit-content",
              }}
            >
              {item.action}
            </span>
            {item.status && item.status !== "SUCCESS" && (
              <span style={{ fontSize: "10px", color: "#991b1b", fontWeight: 600 }}>
                {item.status}
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "Module / Entity",
      key: "entity",
      width: "16%",
      render: (item: any) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          {item.module && (
            <span
              style={{
                background: "#f1f5f9",
                color: "#475569",
                padding: "2px 8px",
                borderRadius: "6px",
                fontSize: "11px",
                fontWeight: 600,
                width: "fit-content",
              }}
            >
              {item.module}
            </span>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Database size={13} color="#94a3b8" />
            <span style={{ fontWeight: 500, color: "#334155", fontSize: "13px" }}>
              {item.entity}
            </span>
            {item.entityId != null && (
              <span style={{ fontSize: "11px", color: "#94a3b8" }}>
                #{item.entityId}
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "What changed",
      key: "details",
      width: "29%",
      render: (item: any) => {
        const changes = item.changes;
        const changeKeys =
          changes && typeof changes === "object" ? Object.keys(changes) : [];

        return (
          <div style={{ fontSize: "13px", color: "#475569" }}>
            {item.description && (
              <div
                style={{ color: "#0f172a", fontWeight: 600, marginBottom: "4px" }}
              >
                {item.description}
              </div>
            )}

            {changeKeys.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                {changeKeys.slice(0, 4).map((field) => (
                  <div
                    key={field}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "12px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span style={{ fontWeight: 600, color: "#334155" }}>
                      {field}:
                    </span>
                    <span
                      style={{
                        color: "#991b1b",
                        background: "#fef2f2",
                        padding: "0 4px",
                        borderRadius: "4px",
                      }}
                    >
                      {formatValue(changes[field]?.from)}
                    </span>
                    <ArrowRight size={11} color="#94a3b8" />
                    <span
                      style={{
                        color: "#166534",
                        background: "#f0fdf4",
                        padding: "0 4px",
                        borderRadius: "4px",
                      }}
                    >
                      {formatValue(changes[field]?.to)}
                    </span>
                  </div>
                ))}
                {changeKeys.length > 4 && (
                  <span style={{ fontSize: "11px", color: "#94a3b8" }}>
                    +{changeKeys.length - 4} more field(s)
                  </span>
                )}
              </div>
            ) : (
              !item.description && (
                <span style={{ fontStyle: "italic", color: "#94a3b8" }}>
                  {item.oldData?.name || item.newData?.name || "No details recorded"}
                </span>
              )
            )}

            <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "4px" }}>
              {item.method ? `${item.method} ${item.path || ""} · ` : ""}IP:{" "}
              {item.ipAddress || "—"}
            </div>

            {item.action === "DELETE" && item.oldData && (
              <button
                onClick={() => handleRestore(item.id)}
                style={{
                  marginTop: "6px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "3px 8px",
                  borderRadius: "6px",
                  background: "#eff6ff",
                  color: "#1e40af",
                  border: "1px solid #dbeafe",
                  fontSize: "11px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                <RotateCcw size={12} /> Restore
              </button>
            )}
          </div>
        );
      },
    },
    {
      title: "Timestamp",
      key: "createdAt",
      width: "14%",
      render: (item: any) => (
        <div style={{ fontSize: "12px", color: "#64748b" }}>
          {new Date(item.createdAt).toLocaleString()}
        </div>
      ),
    },
  ];

  const filterStyle = {
    padding: "8px 12px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    fontSize: "14px",
    color: "#475569",
    outline: "none",
    background: "white",
    minWidth: "140px",
  };

  return (
    <div style={{ padding: "20px" }}>
      <Header title="Audit Logs" />

      {/* Filter Bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginTop: "20px",
          background: "white",
          padding: "15px 20px",
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          border: "1px solid #f1f5f9",
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: "#64748b",
          }}
        >
          <Filter size={18} />
          <span style={{ fontWeight: 600, fontSize: "14px" }}>Filters:</span>
        </div>

        <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
          <Search
            size={15}
            color="#94a3b8"
            style={{ position: "absolute", left: "10px" }}
          />
          <input
            type="text"
            placeholder="Search description / admin…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ ...filterStyle, paddingLeft: "32px", minWidth: "220px" }}
          />
        </div>

        <select
          name="action"
          value={filters.action}
          onChange={handleFilterChange}
          style={filterStyle}
        >
          <option value="">All Actions</option>
          <option value="CREATE">Create</option>
          <option value="UPDATE">Update</option>
          <option value="DELETE">Delete</option>
          <option value="APPROVE">Approve</option>
          <option value="REJECT">Reject</option>
          <option value="VERIFY">Verify</option>
          <option value="STATUS_UPDATE">Status Update</option>
        </select>

        <select
          name="module"
          value={filters.module}
          onChange={handleFilterChange}
          style={filterStyle}
        >
          <option value="">All Modules</option>
          {MODULES.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>

        <select
          name="entity"
          value={filters.entity}
          onChange={handleFilterChange}
          style={filterStyle}
        >
          <option value="">All Entities</option>
          <option value="categories">Categories</option>
          <option value="products">Products</option>
          <option value="users">Users</option>
          <option value="banners">Banners</option>
          <option value="coupons">Coupons</option>
          <option value="deals">Deals</option>
          <option value="orders">Orders</option>
          <option value="withdrawals">Withdrawals</option>
          <option value="roles">Roles</option>
          <option value="modules">Modules</option>
          <option value="zones">Zones</option>
        </select>

        <input
          type="date"
          name="startDate"
          value={filters.startDate}
          onChange={handleFilterChange}
          style={filterStyle}
          title="From date"
        />
        <input
          type="date"
          name="endDate"
          value={filters.endDate}
          onChange={handleFilterChange}
          style={filterStyle}
          title="To date"
        />

        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              padding: "8px 12px",
              borderRadius: "8px",
              background: "#fee2e2",
              color: "#991b1b",
              border: "none",
              fontSize: "13px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <X size={14} />
            Clear
          </button>
        )}
      </div>

      <div
        style={{
          background: "white",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          marginTop: "15px",
        }}
      >
        <Table
          data={logs}
          columns={columns}
          loading={loading}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={15}
          pageControlled={page}
          onPageChange={(p: number) => setPage(p)}
          hasSearchbar={false}
        />
      </div>
    </div>
  );
};

export default AuditLogTable;
